<template>
    <div
        class="mt-8 grid h-auto w-full gap-2"
        :class="
            columns === 2 ? 'grid-cols-1 gap-4 sm:grid-cols-2' : 'grid-cols-1'
        "
    >
        <div
            v-for="(item, index) in items"
            :key="index"
            class="relative flex h-auto w-full flex-col items-center justify-between space-x-2 rounded-md border border-gray-200 p-8 shadow-sm"
        >
            <div
                class="absolute top-2 right-2 flex h-auto w-auto items-center space-x-2"
            >
                <button
                    class="h-auto w-auto rounded-md bg-blue-500 px-2 py-2 text-sm font-medium text-white enabled:hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-30"
                    type="button"
                    :disabled="index === 0"
                    @click="moveItemUp(index)"
                >
                    <fa icon="fa-chevron-up" />
                </button>
                <button
                    class="h-auto w-auto rounded-md bg-blue-500 px-2 py-2 text-sm font-medium text-white enabled:hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-30"
                    type="button"
                    :disabled="!items || index === items.length - 1"
                    @click="moveItemDown(index)"
                >
                    <fa icon="fa-chevron-down" />
                </button>
                <button
                    class="bg-danger-500 hover:bg-danger-600 h-auto w-auto rounded-md px-4 py-2 text-sm font-medium text-white"
                    @click="removeItem(index)"
                >
                    <fa icon="fa-trash-can" />
                </button>
            </div>
            <div
                v-for="field in fields"
                :key="field.key"
                class="flex h-auto w-full flex-col items-start justify-start"
            >
                <component
                    :is="field.component"
                    v-model="(item as Record<string, unknown>)[field.key]"
                    :name="`${namePrefix}_${index}_${field.key}`"
                    :label="field.label"
                    :help="field.help"
                    class="w-full"
                    v-bind="field.props"
                />
            </div>
        </div>
        <div class="col-span-full flex h-auto w-full items-center justify-end">
            <button
                class="h-auto w-auto rounded-md bg-blue-500 px-4 py-2 text-sm font-medium text-white"
                @click="addItem"
            >
                {{ addLabel }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import type { Component } from 'vue'

export interface RepeatableFieldSchema {
    key: string
    component: Component
    label: string
    help: string
    default: unknown
    props?: Record<string, unknown>
}

const props = withDefaults(
    defineProps<{
        modelValue: unknown[]
        fields: RepeatableFieldSchema[]
        namePrefix: string
        addLabel?: string
        columns?: 1 | 2
    }>(),
    {
        addLabel: 'Add',
        columns: 1,
    }
)

const emit = defineEmits<{
    (e: 'update:modelValue', value: unknown[]): void
}>()

const items = useVModel(props, 'modelValue', emit)

const addItem = () => {
    if (!items.value) {
        items.value = []
    }
    const item: Record<string, unknown> = {}
    props.fields.forEach((field) => {
        item[field.key] = field.default
    })
    items.value.push(item)
}

const removeItem = (index: number) => {
    items.value?.splice(index, 1)
}

// Order is implied purely by array position (e.g. company_milestones, a
// jsonb column with no separate position field), so moving an item is just
// swapping it with its neighbour.
const swapItems = (from: number, to: number) => {
    if (!items.value || to < 0 || to >= items.value.length) {
        return
    }
    const [moved] = items.value.splice(from, 1)
    items.value.splice(to, 0, moved)
}

const moveItemUp = (index: number) => swapItems(index, index - 1)
const moveItemDown = (index: number) => swapItems(index, index + 1)
</script>
